import { NavLink, useNavigate } from 'react-router-dom'
import { roleAtLeast, useAuth } from '../../lib/auth'
import { useDirty } from '../../lib/dirty'
import { Button } from '../ui/Button'
import { ErrorBoundary } from '../ui/ErrorBoundary'

/**
 * `need` = vai **tối thiểu** để API cho đi qua (`requireManager` / `requireAdmin`).
 *
 * Lọc ở đây **không phải** phân quyền — phân quyền thật nằm ở API. Đây chỉ để người dùng
 * không nhìn thấy những mục mà bấm vào đâu cũng 403 mà không hiểu vì sao:
 *   · editor  thấy: Tổng quan · Hình ảnh · Bài viết (+ Tài khoản ở chân sidebar)
 *   · manager thấy thêm: Sản phẩm · Danh mục · Màu sắc · Video
 *   · admin   thấy tất cả
 */
const NAV: { to: string; label: string; icon: string; end?: boolean; need?: 'manager' | 'admin' }[] = [
  { to: '/', label: 'Tổng quan', icon: '▦', end: true },
  { to: '/products', label: 'Sản phẩm', icon: '◈', need: 'manager' },
  { to: '/categories', label: 'Danh mục', icon: '⌥', need: 'manager' },
  { to: '/colors', label: 'Màu sắc', icon: '◐', need: 'manager' },
  { to: '/videos', label: 'Video', icon: '▶', need: 'manager' },
  { to: '/media', label: 'Hình ảnh', icon: '▣' },
  { to: '/trang-chu', label: 'Trang chủ', icon: '⌂', need: 'admin' },
  { to: '/posts', label: 'Bài viết', icon: '✎' },
  { to: '/qua-tang', label: 'Quà tặng', icon: '✿', need: 'admin' },
  { to: '/redirects', label: 'Điều hướng', icon: '⇥', need: 'admin' },
  { to: '/seo', label: 'Sức khoẻ SEO', icon: '◎', need: 'admin' },
  { to: '/settings', label: 'Cài đặt SEO', icon: '⚙', need: 'admin' },
]

export function Shell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth()
  const nav = NAV.filter((n) => !n.need || roleAtLeast(user?.role, n.need))
  const navigate = useNavigate()
  const { dirty, confirmLeave } = useDirty()
  /** Form đang sửa dở → chặn click, hỏi trước rồi mới điều hướng. */
  const guard = (to: string) => (e: React.MouseEvent) => {
    if (!dirty) return
    e.preventDefault()
    confirmLeave(() => navigate(to))
  }

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-white md:flex">
        <div className="flex items-center gap-2.5 px-5 py-4">
          <img src="/logo.png" alt="" className="size-9 rounded-full object-cover" />
          <div className="leading-tight">
            <div className="font-heading text-sm font-bold text-ink">Nhà Nhỏ</div>
            <div className="text-2xs uppercase tracking-wider text-ink-muted">Quản trị</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={guard(n.to)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-button px-3 py-2 text-sm transition-colors ${
                  isActive ? 'bg-primary-100 font-medium text-primary-950' : 'text-ink-muted hover:bg-sand hover:text-ink'
                }`
              }
            >
              <span className="w-4 text-center text-ink-faint">{n.icon}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <NavLink to="/account" onClick={guard('/account')} className="block rounded-button px-3 py-2 text-sm hover:bg-sand">
            <div className="truncate font-medium text-ink">{user?.name}</div>
            <div className="truncate text-xs text-ink-muted">{user?.email}</div>
          </NavLink>
          <Button variant="ghost" size="sm" className="mt-1 w-full justify-start" onClick={() => confirmLeave(() => void logout().then(() => navigate('/login')))}>
            Đăng xuất
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Thanh trên cho mobile */}
        <header className="flex items-center justify-between border-b border-border bg-white px-4 py-3 md:hidden">
          <span className="font-heading font-bold text-ink">Nhà Nhỏ · Quản trị</span>
          <select
            className="rounded-input border border-border-strong px-2 py-1 text-sm"
            onChange={(e) => { const to = e.target.value; confirmLeave(() => navigate(to)) }}
            defaultValue=""
            aria-label="Điều hướng"
          >
            <option value="" disabled>Menu</option>
            {nav.map((n) => <option key={n.to} value={n.to}>{n.label}</option>)}
            <option value="/account">Tài khoản</option>
          </select>
        </header>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-8"><ErrorBoundary>{children}</ErrorBoundary></main>
      </div>
    </div>
  )
}
